/**
 * Password reset by one-time code.
 *
 * Two steps: ask for the email, then take the code and the new password together. The
 * second step never re-asks for the address, because someone reading a code off their
 * phone should not also have to retype an email they just entered.
 *
 * The first step's confirmation is deliberately the same whether or not the address
 * exists. Cognito's `resetPassword` throws UserNotFoundException, and surfacing that
 * turns this form into a way to test which addresses belong to employees.
 */

import { useState, type FormEvent } from 'react';

import { useI18n } from '@/i18n';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';

import { requestInvitation } from '@/lib/api';

import { authConfigured } from './config';
import { authErrorKey } from './errors';

const FIELD =
  'w-full h-11 px-3 rounded-lg bg-surface border border-border text-text text-sm ' +
  'outline-none transition-colors placeholder:text-subtle ' +
  'focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/25';

const BUTTON =
  'w-full h-11 rounded-lg bg-primary text-white text-sm font-semibold transition-opacity ' +
  'hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed';

export function ForgotPassword({
  initialEmail,
  onDone,
  onCancel,
}: {
  initialEmail: string;
  /** Called with a message for the sign-in page to show once the password is changed. */
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();

  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSend = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);

    void (async () => {
      try {
        await resetPassword({ username: email.trim() });
      } catch (err) {
        const name = (err as { name?: string }).name;
        /*
         * Move on regardless for "no such user" — see the file comment. Rate limiting
         * and genuine failures still surface, because silently doing nothing when the
         * service is down leaves someone waiting for an email that will never arrive.
         */
        /*
         * An account that has never set a password cannot reset one — Cognito says
         * "cannot be reset in the current state". That is someone whose invitation
         * lapsed, so send them a new one instead of an error they cannot act on.
         */
        if (name === 'NotAuthorizedException' || name === 'InvalidParameterException') {
          await requestInvitation(email.trim()).catch(() => undefined);
          setBusy(false);
          onDone(t('auth.forgotFlow.invitationSent'));
          return;
        }
        if (name !== 'UserNotFoundException') {
          setError(t(authErrorKey(err)));
          setBusy(false);
          return;
        }
      }
      setBusy(false);
      setStage('code');
    })();
  };

  const onConfirm = (e: FormEvent) => {
    e.preventDefault();

    if (password !== confirm) {
      setError(t('auth.newPassword.mismatch'));
      return;
    }

    setBusy(true);
    setError(null);

    void (async () => {
      try {
        await confirmResetPassword({
          username: email.trim(),
          confirmationCode: code.trim(),
          newPassword: password,
        });
        onDone(t('auth.forgotFlow.done'));
      } catch (err) {
        setError(t(authErrorKey(err)));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <>
      <h1 className="text-xl font-bold text-center">
        {stage === 'email' ? t('auth.forgotFlow.title') : t('auth.forgotFlow.codeTitle')}
      </h1>
      <p className="mt-1.5 text-center text-sm text-muted">
        {stage === 'email' ? t('auth.forgotFlow.subtitle') : t('auth.forgotFlow.codeSubtitle')}
      </p>

      {stage === 'code' ? (
        <p
          className="mt-4 rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm text-muted"
          role="status"
        >
          {t('auth.forgotFlow.sent')}
        </p>
      ) : null}

      {error ? (
        <p
          className="mt-4 rounded-lg border border-[var(--color-danger)] bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {stage === 'email' ? (
        <form onSubmit={onSend} className="mt-5 space-y-3.5">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted">{t('auth.email')}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              className={FIELD}
              placeholder="nome@hhelectrical.it"
            />
          </label>

          <button type="submit" disabled={busy || !authConfigured} className={BUTTON}>
            {busy ? t('auth.signingIn') : t('auth.forgotFlow.send')}
          </button>
        </form>
      ) : (
        <form onSubmit={onConfirm} className="mt-5 space-y-3.5">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted">
              {t('auth.forgotFlow.code')}
            </span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              className={`${FIELD} text-center text-lg font-mono tracking-[0.4em]`}
              placeholder="000000"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted">
              {t('auth.forgotFlow.newPassword')}
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={12}
              className={FIELD}
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted">
              {t('auth.forgotFlow.confirm')}
            </span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              minLength={12}
              className={FIELD}
            />
          </label>

          <p className="text-xs text-subtle">{t('auth.newPassword.rules')}</p>

          <button type="submit" disabled={busy || code.length < 6} className={BUTTON}>
            {busy ? t('auth.signingIn') : t('auth.forgotFlow.submit')}
          </button>
        </form>
      )}

      <button
        type="button"
        onClick={onCancel}
        className="mt-3.5 w-full text-center text-sm text-muted hover:text-primary transition-colors"
      >
        {t('auth.back')}
      </button>
    </>
  );
}
